import { Browser, detect } from 'detect-browser'
import devtools from 'devtools-detect'

import { AppMode } from '@collector/shared-utils'

const baseFontSize = 16
let scaledFontSize = 16

function setHtmlRootScaledFontSize(
  layoutBaseHeight: number,
  height?: number,
): void {
  scaledFontSize = Number(
    (getScaledRemFactor(layoutBaseHeight, height) * baseFontSize).toFixed(2),
  )

  document.documentElement.style.fontSize = `${scaledFontSize}px`
}

function getZoomFactor(): number {
  // for some strange reason, outer width is 0 in some cases
  // this is probably bug in Chromium
  // https://issues.chromium.org/issues/41317497
  //
  // in this case, we return 1 which actually works like without zoom
  if (window.outerWidth === 0) return 1

  return window.outerWidth / window.innerWidth
}

/**
 * Return `true` if browser can properly calculate the zoom factor
 */
function isReliableBrowserZoomFactor(): boolean {
  const browser = detect()
  const supportedBrowsers: Browser[] = ['chrome', 'safari', 'edge-chromium']

  return supportedBrowsers.includes(browser?.name as Browser)
}

/**
 * Calculate root element font-size factor for scaling the layout based on the screen size
 *
 * @param layoutBaseHeight - The base height used for scaling the layout.
 *
 * The base height is used to properly scale the layout for different resolutions.
 * This value is adjusted through trial and error to ensure the design looks good
 * on most screens when scaled.
 *
 * A larger value will make the layout appear smaller; a smaller value will make it appear larger.
 * Originally, this value was related to 'base height' in Figma's design.
 */
export function getScaledRemFactor(
  layoutBaseHeight: number,
  height?: number,
): number {
  const zoomFactor = isReliableBrowserZoomFactor() ? getZoomFactor() : 1
  const zoomedHeight = (height || window.innerHeight) * zoomFactor

  return zoomedHeight / layoutBaseHeight
}

export function getScaledPx(px: number): number {
  const remFactor = scaledFontSize / baseFontSize
  const scaledPx = px * remFactor

  return Number(scaledPx.toFixed(2))
}

/**
 * Scale the base layout for different resolutions, to always fit full screen
 */
export function scaleLayout(
  environment: AppMode,
  layoutBaseHeight: number,
): void {
  const ignoredAppModes: AppMode[] = ['development', 'beta']

  // do not scale the layout when devtools are open, because it makes it difficult to inspect other problems
  if (devtools.isOpen && ignoredAppModes.includes(environment)) {
    return
  }

  // new tab on Chromium-based browsers has 0 outerHeight
  if (window.outerHeight === 0) {
    setHtmlRootScaledFontSize(layoutBaseHeight)
    return
  }

  // window height that can be taken by browser, when window fills maximum screen height
  const maxWindowHeight =
    screen.availHeight - window.outerHeight + window.innerHeight

  // scale the layout as it would be maximized
  setHtmlRootScaledFontSize(layoutBaseHeight, maxWindowHeight)
}
